import axios from 'axios';

import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get, patch } from 'utils/api';
import { snakeCaseKeys } from 'utils/keys';
import { Querystring } from 'utils/url';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';

import type {
  Action,
  AdminSentimentAnalysisQuery,
  Dispatch,
  Pagination,
  SentimentAnalysisItem,
  SentimentClass,
  ThunkAction
} from 'types';

applyTranslations(require('./admin-sentiment-analysis.i18n.json'));

const LOADING = 'adminSentimentAnalysis/LOADING';
const LOADED = 'adminSentimentAnalysis/LOADED';
const RESET = 'adminSentimentAnalysis/RESET';
const SENTIMENT_CLASS_CHANGED = 'adminSentimentAnalysis/SENTIMENT_CLASS_CHANGED';

const ITEMS_PER_PAGE = 100;

type State = {
  list: Array<SentimentAnalysisItem>,
  isLoading: boolean,
  pagination: Pagination
};

const initialState: State = {
  isLoading: false,
  list: [],
  pagination: {}
};

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

export function resetSentimentAnalysis(): Action {
  return {
    type: RESET
  };
}

function sentimentAnalysisResultsLoaded(data): Action {
  return {
    type: LOADED,
    payload: {
      data
    }
  };
}

function sentimentClassChanged(commentUuid: string, sentimentClass: SentimentClass): Action {
  return {
    type: SENTIMENT_CLASS_CHANGED,
    payload: {
      commentUuid,
      sentimentClass
    }
  };
}

let fetchSentimentAnalysisCancelToken;

export function fetchSentimentAnalysis(
  query: AdminSentimentAnalysisQuery,
  silent: boolean,
  includeAdditional?: boolean
): ThunkAction {
  return async (dispatch: Dispatch) => {
    let baseUrl = '';

    const {
      deliverableId,
      campaignId,
      creatorId,
      taskTypes,
      sort
    } = query;

    let params = snakeCaseKeys({
      page: query.page || 1,
      itemsPerPage: ITEMS_PER_PAGE,
      filterBy: decodeURIComponent(query.sentimentClass || ''),
      taskTypes,
      sort,
      includeAdditional
    });

    if (deliverableId) {
      baseUrl = API.END_POINTS.ADMIN.getSentimentAnalysisDeliverableResults(deliverableId);
    } else if (campaignId && creatorId) {
      params = {
        ...params,
        ...snakeCaseKeys({
          creatorIds: creatorId
        })
      };

      baseUrl = API.END_POINTS.ADMIN.getSentimentAnalysisCampaignResults(campaignId);
    } else if (campaignId) {
      baseUrl = API.END_POINTS.ADMIN.getSentimentAnalysisCampaignResults(campaignId);
    } else {
      return;
    }

    const queryString = Querystring.objectToString(params);
    const url = `${baseUrl}?${queryString}`;

    // handle cancellation in case of repeated request
    if (fetchSentimentAnalysisCancelToken) {
      fetchSentimentAnalysisCancelToken.cancel();
    }
    fetchSentimentAnalysisCancelToken = axios.CancelToken.source();

    // do request
    if (!silent) {
      dispatch(setLoadingStatus(true));
    }

    try {
      const response = await get(url, { cancelToken: fetchSentimentAnalysisCancelToken.token }, { suppressGlobalNotification: true });
      dispatch(sentimentAnalysisResultsLoaded(response.data));
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      dispatch(setLoadingStatus(false));
      dispatch(resetSentimentAnalysis());
      logError(error);
      throw error;
    }
  };
}

export function changeSentimentClass(deliverableId: number, commentUuid: string, sentimentClass: SentimentClass): ThunkAction {
  const url = API.END_POINTS.ADMIN.changeSentimentClass(deliverableId, commentUuid);

  return async (dispatch: Dispatch) => {
    try {
      await patch(url, { data: { sentimentClass } }, { suppressGlobalNotification: true });
      dispatch(sentimentClassChanged(commentUuid, sentimentClass));
      dispatch(success(__t('admin_sentiment_analysis_reducer.message_sentiment_class_changed_success'), { hideDuration: 2000 }));
    } catch (error) {
      dispatch(danger(__t('admin_sentiment_analysis_reducer.message_sentiment_class_changed_error')));
      logError(error);
      throw error;
    }
  };
}

export default createReducer<State, any>('adminSentimentAnalysis', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [LOADED]: (state, { data }) => ({
    ...state,
    list: data.data.map(item => ({
      ...item,
      id: item.commentUuid
    })),
    pagination: {
      ...data.meta,
      links: data.links
    },
    isLoading: false
  }),
  [SENTIMENT_CLASS_CHANGED]: (state, { commentUuid, sentimentClass }) => ({
    ...state,
    list: state.list.map(item => (item.commentUuid === commentUuid ? { ...item, sentimentClass } : item))
  })
});
